import { cloneVNode, computed, h, resolveDynamicComponent, unref, type VNode } from "vue";
import { RouterLink, useRoute, type RouterLinkProps, type useLink } from "vue-router";

export interface RouterProps extends Partial<RouterLinkProps> {
	matchQuery?: boolean;
	href?: string;
}

export function useRouterLink(props: RouterProps) {
	const currentRoute = useRoute();

	const RouterLinkComponent = resolveDynamicComponent("RouterLink") as typeof RouterLink | string;

	const routerLinkProps = computed<RouterLinkProps | null>(() => {
		if (props.to == null) {
			return null;
		}

		return {
			to: props.to,
			replace: props.replace,
		};
	});

	const isLink = computed(() => routerLinkProps.value != null || !!props.href);

	function getTag(defaultTag: any) {
		if (isLink.value) {
			return "a";
		}
		return defaultTag;
	}

	function renderLink(node: VNode): VNode {
		if (routerLinkProps.value == null || typeof RouterLinkComponent === "string") {
			return node;
		}

		return h(
			RouterLinkComponent,
			{
				...routerLinkProps.value,
				custom: true,
			} as any,
			{
				default: ({ href, navigate, isActive, isExactActive }: ReturnType<typeof useLink>) => {
					let classes = "";

					let isQueryMatched = true;
					if (props.matchQuery) {
						const query = new URLSearchParams(unref(href).split("?").at(1));
						for (const [key, value] of query.entries()) {
							if (currentRoute.query[key] !== value) {
								isQueryMatched = false;
								break;
							}
						}
					}

					if (unref(isActive) && isQueryMatched) {
						classes += `${props.activeClass || ""}`;
					}
					if (unref(isExactActive) && isQueryMatched) {
						classes += `${props.exactActiveClass || ""}`;
					}

					return cloneVNode(node, {
						class: classes,
						href: unref(href),
						onClick: navigate,
					});
				},
			}
		);
	}

	return { isLink, getTag, renderLink };
}
